import { apiFetch, buildQueryString, type PaginatedListResponse } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';
import type { User } from '@lognai/core-platform';
import type { PasswordLink, PasswordLinkView } from '@lognai/plugin-admin';
import { UserStatus } from '../types/domain-enums';

export interface UserFilter {
  companyId?: string;
  status?: UserStatus;
  department?: string;
  search?: string;
}

export type UserFilters = ListQueryParams<UserFilter>;

/**
 * The canonical paginated envelope, as returned by every admin list endpoint:
 * { items, pagination }. (Previously the users endpoint leaked the
 * operation's internal flat shape — total/page/pageSize — making it the one
 * list with bespoke pagination fields.)
 */
export type PaginatedUsersResult = PaginatedListResponse<User>;

export interface UserCreateInput {
  email: string;
  fullName: string;
  companyId: string;
  username?: string;
  department?: string;
  jobTitle?: string;
  employeeId?: string;
  phoneNumber?: string;
  status?: UserStatus;
  timezone?: string;
  language?: string;
  /** Company role granted in the same request. */
  roleId?: string;
}

/**
 * What create returns: the user, plus the one-time set-password link. The
 * link is absent when generating it failed after the user was saved — the
 * admin then generates one from the user's menu.
 */
export type CreatedUser = User & { passwordLink?: PasswordLink };

export interface UserUpdateInput {
  fullName?: string;
  department?: string;
  jobTitle?: string;
  employeeId?: string;
  phoneNumber?: string;
  timezone?: string;
  language?: string;
  status?: UserStatus;
}

export interface UserRoleAssignment {
  roleId: string;
  companyId?: string;
  expiresAt?: Date;
}

export interface UserAccessAssignment {
  accessId: string;
  companyId?: string;
  expiresAt?: Date;
}

const BASE_URL = '/api/admin/users';

export const usersService = {
  /**
   * List users with optional filters and server-side pagination.
   *
   * Returns the paginated envelope so callers can render `total`/`totalPages`
   * without a second round trip.
   *
   * Page size goes over the wire as `limit`, like every other admin list. This
   * used to rename `limit` to `pageSize` first, which `buildQueryString` then
   * dropped on the floor — `pageSize` is not in its flat-key allowlist. The
   * request went out as `?page=N` alone, the server fell back to 100 rows, and
   * the pager still rendered against a limit of 20: page 2 of a 20-row table
   * actually returned rows 101-200.
   */
  list: async (filters?: UserFilters): Promise<PaginatedUsersResult> => {
    const query = buildQueryString(filters as Record<string, unknown> | undefined);
    // No array fallback: the only route this calls (GET /api/admin/users →
    // listUsersHandler) has never returned a bare array, and synthesizing
    // pagination from an array is unsound anyway — array length can't be
    // trusted as the total, so a page-sized array would silently disable
    // pagination and hide the rest of the results.
    return apiFetch<PaginatedUsersResult>(`${BASE_URL}${query}`);
  },

  /**
   * Get a specific user by ID
   */
  getById: async (userId: string): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}`);
  },

  /**
   * Create a new user
   */
  create: async (data: UserCreateInput): Promise<CreatedUser> => {
    return apiFetch<CreatedUser>(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update a user
   */
  update: async (userId: string, data: UserUpdateInput): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a user
   */
  delete: async (userId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}`, { method: 'DELETE' });
  },

  /**
   * Update user status
   */
  updateStatus: async (userId: string, status: UserStatus): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },

  /**
   * Get user's roles
   */
  getRoles: async (userId: string): Promise<unknown[]> => {
    return apiFetch<unknown[]>(`${BASE_URL}/${userId}/roles`);
  },

  /**
   * Assign a role to user
   */
  assignRole: async (userId: string, assignment: UserRoleAssignment): Promise<unknown> => {
    return apiFetch<unknown>(`${BASE_URL}/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  },

  /**
   * Bulk assign role to multiple users
   */
  bulkAssignRole: async (
    userIds: string[],
    roleId: string,
    assignment?: Partial<UserRoleAssignment>
  ): Promise<{ assigned: number; failed: number }> => {
    return apiFetch<{ assigned: number; failed: number }>(`${BASE_URL}/bulk/assign-role`, {
      method: 'POST',
      body: JSON.stringify({ userIds, roleId, ...assignment }),
    });
  },

  /**
   * Remove a role from user
   */
  removeRole: async (userId: string, roleId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}/roles/${roleId}`, { method: 'DELETE' });
  },

  /**
   * Get user's direct access assignments
   */
  getAccessAssignments: async (userId: string): Promise<unknown[]> => {
    return apiFetch<unknown[]>(`${BASE_URL}/${userId}/access`);
  },

  /**
   * Assign direct access to user
   */
  assignAccess: async (userId: string, assignment: UserAccessAssignment): Promise<unknown> => {
    return apiFetch<unknown>(`${BASE_URL}/${userId}/access`, {
      method: 'POST',
      body: JSON.stringify(assignment),
    });
  },

  /**
   * Remove direct access from user
   */
  removeAccess: async (userId: string, accessId: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${userId}/access/${accessId}`, { method: 'DELETE' });
  },

  /**
   * Get user's effective permissions
   */
  getEffectivePermissions: async (userId: string): Promise<string[]> => {
    return apiFetch<string[]>(`${BASE_URL}/${userId}/permissions`);
  },

  /**
   * Add secondary company to user
   */
  addSecondaryCompany: async (userId: string, companyId: string): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}/secondary-companies`, {
      method: 'POST',
      body: JSON.stringify({ companyId }),
    });
  },

  /**
   * Remove secondary company from user
   */
  removeSecondaryCompany: async (userId: string, companyId: string): Promise<User> => {
    return apiFetch<User>(`${BASE_URL}/${userId}/secondary-companies/${companyId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Generate a one-time password link for a user. The raw token is in this
   * response only — the server keeps its hash, so it can never be shown again.
   * Admins never set another user's password.
   */
  generatePasswordLink: async (userId: string): Promise<PasswordLink> => {
    return apiFetch<PasswordLink>(`${BASE_URL}/${userId}/password-link`, { method: 'POST' });
  },

  /** The user's password-link history, newest first. */
  getPasswordLinks: async (userId: string): Promise<PasswordLinkView[]> => {
    return apiFetch<PasswordLinkView[]>(`${BASE_URL}/${userId}/password-links`);
  },

  /**
   * Email the user a password reset link. Reuses the public forgot-password
   * flow (token + email + /reset-password page) rather than a parallel admin
   * one, so there is one reset link format and one expiry rule.
   */
  sendPasswordResetLink: async (email: string): Promise<void> => {
    await apiFetch<unknown>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },


};
